import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  TextField,
  InputAdornment,
  Collapse,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  EnhancedEncryption as VaultIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayArrow as PlayIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Apps as AppsIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import { API_BASE_URL } from "../config";

const CredentialVaultPage = () => {
  const [credentials, setCredentials] = useState([]);
  const [extensionScripts, setExtensionScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedApps, setExpandedApps] = useState({});
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [credRes, scriptRes] = await Promise.all([
          fetch(`${API_BASE_URL}/credential-vault`, { headers: getAuthHeaders() }),
          fetch(`${API_BASE_URL}/extension-scripts`, { headers: getAuthHeaders() }),
        ]);

        if (!credRes.ok) throw new Error(`Credentials: ${credRes.status}`);
        const credData = await credRes.json();
        setCredentials(credData);

        if (scriptRes.ok) {
          const scriptData = await scriptRes.json();
          setExtensionScripts(scriptData);
        }

        setError(null);
      } catch (err) {
        console.error("Failed to load vault data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Group credentials by app name (derived from "AppName - username" pattern)
  const groupedCredentials = useMemo(() => {
    const groups = {};
    for (const cred of credentials) {
      const appName = cred.name.includes(" - ")
        ? cred.name.split(" - ")[0].trim()
        : cred.name;
      if (!groups[appName]) {
        groups[appName] = [];
      }
      groups[appName].push(cred);
    }
    return groups;
  }, [credentials]);

  // Build a map of appName -> login scripts
  const appLoginScripts = useMemo(() => {
    const map = {};
    const loginScripts = extensionScripts.filter((s) => s.type === "login");
    for (const script of loginScripts) {
      // Match by script name pattern (e.g. "Khan Academy Login" -> "Khan Academy")
      const scriptAppName = script.name.replace(/ Login$/i, "").trim();
      map[scriptAppName] = script;

      // Also map by appKey for convenience
      if (script.appKey) {
        map[`__appKey__${script.appKey}`] = script;
      }
    }
    return map;
  }, [extensionScripts]);

  // Find matching login script for an app name
  const findLoginScript = (appName) => {
    // Direct name match
    if (appLoginScripts[appName]) return appLoginScripts[appName];

    // Case-insensitive match
    const lowerApp = appName.toLowerCase();
    for (const [key, script] of Object.entries(appLoginScripts)) {
      if (key.toLowerCase() === lowerApp) return script;
    }

    // Partial match (e.g. "READTHEORY" matches "ReadTheory Login")
    for (const [key, script] of Object.entries(appLoginScripts)) {
      if (
        !key.startsWith("__appKey__") &&
        (key.toLowerCase().includes(lowerApp) ||
          lowerApp.includes(key.toLowerCase()))
      ) {
        return script;
      }
    }

    return null;
  };

  // Count all scripts (not just login) for an app
  const getAppScriptCount = (appName) => {
    const loginScript = findLoginScript(appName);
    if (!loginScript || !loginScript.appKey) return 0;
    return extensionScripts.filter((s) => s.appKey === loginScript.appKey).length;
  };

  // Filter by search
  const filteredApps = useMemo(() => {
    const query = searchQuery.toLowerCase();
    if (!query) return Object.keys(groupedCredentials).sort();
    return Object.keys(groupedCredentials)
      .filter((appName) => {
        if (appName.toLowerCase().includes(query)) return true;
        return groupedCredentials[appName].some(
          (c) =>
            c.data?.username?.toLowerCase().includes(query) ||
            c.name.toLowerCase().includes(query)
        );
      })
      .sort();
  }, [groupedCredentials, searchQuery]);

  const toggleApp = (appName) => {
    setExpandedApps((prev) => ({ ...prev, [appName]: !prev[appName] }));
  };

  const togglePassword = (credId) => {
    setRevealedPasswords((prev) => ({ ...prev, [credId]: !prev[credId] }));
  };

  const handleRunLoginTest = (appName, credential) => {
    const script = findLoginScript(appName);
    if (!script) {
      setSnackbarMessage(`No login script found for ${appName}`);
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    setSnackbarMessage(
      `Login test queued for ${credential.data?.username || credential.name} on ${appName}`
    );
    setSnackbarSeverity("info");
    setSnackbarOpen(true);

    // In the future, this will trigger the extension script runner
    console.log("Run login test:", {
      scriptId: script.id,
      credentialId: credential.id,
      appName,
      username: credential.data?.username,
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading credentials: {error}
      </Alert>
    );
  }

  const totalCredentials = credentials.length;
  const totalApps = Object.keys(groupedCredentials).length;

  return (
    <div>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <VaultIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography variant="h4">Credentials</Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Encrypted credential storage for app automations
        </Typography>
      </Box>

      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <LockIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">{totalCredentials}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Encrypted Credentials
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <AppsIcon color="secondary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">{totalApps}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Applications
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <PeopleIcon color="success" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">
                  {totalCredentials > 0
                    ? Math.round(totalCredentials / totalApps)
                    : 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Credentials / App
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search by app name or username..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* App groups */}
      {filteredApps.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            {searchQuery
              ? "No credentials match your search."
              : "No credentials found."}
          </Typography>
        </Paper>
      ) : (
        filteredApps.map((appName) => {
          const appCreds = groupedCredentials[appName];
          const isExpanded = expandedApps[appName];
          const loginScript = findLoginScript(appName);
          const scriptCount = getAppScriptCount(appName);

          return (
            <Paper key={appName} elevation={2} sx={{ mb: 2 }}>
              {/* App header row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  p: 2,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                onClick={() => toggleApp(appName)}
              >
                <IconButton size="small" sx={{ mr: 1 }}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>

                <Box sx={{ flexGrow: 1, minWidth: 120 }}>
                  <Typography variant="h6">{appName}</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <Chip
                    label={`${appCreds.length} credential${appCreds.length !== 1 ? "s" : ""}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  {scriptCount > 0 && (
                    <Chip
                      label={`${scriptCount} script${scriptCount !== 1 ? "s" : ""}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {loginScript ? (
                    <Chip label="Login Script" size="small" color="success" />
                  ) : (
                    <Chip
                      label="No Login Script"
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              {/* Expanded credential table */}
              <Collapse in={isExpanded}>
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Username</TableCell>
                        <TableCell>Site</TableCell>
                        <TableCell>Password</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {appCreds.map((cred) => (
                        <TableRow key={cred.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                              {cred.name && cred.name.includes(" - ")
                                ? cred.name.split(" - ").slice(1).join(" - ")
                                : cred.data?.username || cred.nucleosUserUid || cred.name || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {cred.clientId || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: "monospace" }}
                              >
                                {revealedPasswords[cred.id]
                                  ? cred.data?.password || "N/A"
                                  : "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022"}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => togglePassword(cred.id)}
                              >
                                {revealedPasswords[cred.id] ? (
                                  <VisibilityOffIcon fontSize="small" />
                                ) : (
                                  <VisibilityIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={cred.type === "username_password" ? "User/Pass" : cred.type}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(cred.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {loginScript && (
                              <Tooltip title="Run login test for this credential">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<PlayIcon />}
                                  onClick={() =>
                                    handleRunLoginTest(appName, cred)
                                  }
                                >
                                  Test Login
                                </Button>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          );
        })
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default CredentialVaultPage;
